import React, { useState, useEffect, useRef } from "react";
import { api } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import BarcodeScanner from "@/components/BarcodeScanner";
import { toast } from "sonner";

const MaterialAutocomplete = ({ value, onChange, onSelect, placeholder = "Material suchen..." }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value;
    onChange(val);
    setHighlighted(-1);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.length < 2) { setSuggestions([]); setShowSuggestions(false); return; }

    debounceRef.current = setTimeout(async () => {
      try {
        const res = await api.get(`/api/materials?search=${encodeURIComponent(val)}`);
        setSuggestions(res.data.slice(0, 8));
        setShowSuggestions(res.data.length > 0);
      } catch { setSuggestions([]); }
    }, 250);
  };

  const handleSelect = (material) => {
    onSelect(material);
    setShowSuggestions(false);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlighted(h => Math.min(h + 1, suggestions.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setHighlighted(h => Math.max(h - 1, 0)); }
    if (e.key === "Enter" && highlighted >= 0) { e.preventDefault(); handleSelect(suggestions[highlighted]); }
    if (e.key === "Escape") setShowSuggestions(false);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="flex gap-2 items-center">
        <Input
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
          placeholder={placeholder}
          data-testid="material-autocomplete"
          className="flex-1"
        />
        <BarcodeScanner
          title="Material scannen"
          description="Scanne die Artikelnummer-Barcode am Produkt – wird automatisch im Lager gesucht."
          onScan={async (code) => {
            try {
              const res = await api.get(`/api/materials?search=${encodeURIComponent(code)}`);
              const exact = (res.data || []).find(m => (m.artikelnummer || "").trim() === code.trim());
              const match = exact || (res.data || [])[0];
              if (match) {
                onSelect(match);
                toast.success(`"${match.bezeichnung}" übernommen`);
              } else {
                onChange(code);
                toast.info(`Kein Artikel mit "${code}" gefunden – Suche gesetzt.`);
              }
            } catch {
              onChange(code);
            }
          }}
        />
      </div>
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-64 overflow-y-auto" data-testid="material-suggestions">
          {suggestions.map((m, idx) => {
            const isService = m.item_type === "dienstleistung";
            const isOrderSpecific = !!m.is_order_specific && !isService;
            return (
            <div
              key={m.id}
              onClick={() => handleSelect(m)}
              className={`px-3 py-2 cursor-pointer text-sm hover:bg-muted ${idx === highlighted ? 'bg-muted' : ''}`}
              data-testid={`suggestion-${idx}`}
            >
              <div className="flex justify-between items-center">
                <span className="font-medium flex items-center gap-2">
                  {m.bezeichnung}
                  {isService && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-700">
                      Dienstleistung
                    </span>
                  )}
                  {isOrderSpecific && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800" title="Auftragsartikel — kein Lagerbestand">
                      Auftragsartikel
                    </span>
                  )}
                </span>
                <span className="text-green-600 font-medium">{m.verkaufspreis.toFixed(2)} €</span>
              </div>
              <div className="flex gap-3 text-xs text-muted-foreground mt-0.5">
                {m.artikelnummer && <span>Art.Nr: {m.artikelnummer}</span>}
                {m.lieferant && <span>Lief: {m.lieferant}</span>}
                {!isService && !isOrderSpecific && (
                  <span className={m.bestand <= 0 ? 'text-red-600 font-medium' : ''}>Verfügbar: {m.bestand} {m.einheit}</span>
                )}
                {!isService && !isOrderSpecific && (m.bestand_reserviert || 0) > 0 && (
                  <span className="text-amber-700">Reserviert: {m.bestand_reserviert}</span>
                )}
                {(m.usage_count || 0) > 0 && (
                  <span className="text-slate-600">{m.usage_count}× bestellt</span>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MaterialAutocomplete;
